/**
 * Vendor Type Constants — Phase 5B identity layer (frontend mirror).
 *
 * The authoritative source is `/api/vendor-types`; this file mirrors the
 * static structure for label rendering + icon mapping + synchronous client
 * logic (inference for legacy vendors, guided category sorting). Keep in
 * sync with `/app/backend/services/vendor_type.py`.
 *
 * Why mirror at all?
 *   - Icons and UI copy live only on the client.
 *   - The signup screen and the profile picker render immediately without
 *     waiting for a network round-trip; the API is still fetched to supply
 *     the authoritative category bucket at runtime.
 */

import {
  Music,
  Camera,
  Utensils,
  Palette,
  Sparkles,
  Building2,
  PartyPopper,
  ClipboardList,
  HelpCircle,
} from 'lucide-react';

export const VENDOR_TYPE_KEYS = [
  'entertainment',
  'photography_video',
  'food_beverage',
  'decor_design',
  'beauty_styling',
  'venue',
  'rentals',
  'event_services',
  'other',
];

export const VENDOR_TYPE_META = {
  entertainment:     { label: 'Entertainment',       short: 'Entertainment',    description: "DJs, bands, performers, kids' entertainers", icon: Music },
  photography_video: { label: 'Photo & Video',        short: 'Photo & Video',    description: 'Photographers, videographers, photo booths', icon: Camera },
  food_beverage:     { label: 'Food & Beverage',      short: 'Food & Drink',     description: 'Catering, bartending, desserts, food trucks', icon: Utensils },
  decor_design:      { label: 'Decor & Design',       short: 'Decor & Design',   description: 'Balloons, florals, styling, invitations', icon: Palette },
  beauty_styling:    { label: 'Beauty & Styling',     short: 'Beauty',           description: 'Makeup, hair, nails, grooming', icon: Sparkles },
  venue:             { label: 'Venue',                short: 'Venue',            description: 'Event spaces, halls, reception venues', icon: Building2 },
  rentals:           { label: 'Rentals & Equipment',  short: 'Rentals',          description: 'Bounce houses, tents, tables, A/V, games', icon: PartyPopper },
  event_services:    { label: 'Event Services',       short: 'Event Services',   description: 'Planners, coordinators, staff, logistics', icon: ClipboardList },
  other:             { label: 'Something else',       short: 'Other',            description: 'Animals, special effects, unique experiences', icon: HelpCircle },
};

/**
 * Client-side mirror of `VENDOR_TYPE_TO_ALLOWED_CATEGORIES`.
 * Used for the signup 2-step picker (render immediately, before the API
 * response returns) and for guided sorting in the profile form. The profile
 * form also overrides this with the authoritative API response when available.
 */
export const VENDOR_TYPE_TO_ALLOWED_CATEGORIES = {
  entertainment: [
    'DJ', 'Live Band', 'Solo Musician', 'Singer / Vocalist',
    'Choirs / Gospel Groups', 'Comedians', 'Magicians / Illusionists',
    'Dancers / Dance Groups', "Kids' Entertainers", 'Face Painters',
    'Balloon Twisters', 'Mascots / Character Performers',
    'Caricature Artists', 'Cultural Performers', 'Karaoke Providers',
    'Trivia / Game Show Hosts', 'Bingo Hosts',
  ],
  photography_video: [
    'Photographer', 'Videographer', 'Drone Operator',
    'Photo Booth Provider', '360 Photo Booth', 'Event Live-Streaming',
    'Photo & Video Equipment Rentals',
  ],
  food_beverage: [
    'Caterer', 'Food Truck', 'Bartender / Mixologist',
    'Coffee / Beverage Bar', 'Cotton Candy / Popcorn / Snowball Providers',
    'Dessert / Treat Makers', 'Cake Bakers', 'Cookie / Cupcake Makers',
    'Candy & Sweets Table Designers', 'Custom Edible Favors',
  ],
  decor_design: [
    'Balloon Artist', 'Backdrops & Event Walls', 'Event Decor & Styling',
    'Floral Designers', 'Luxury Picnic Setup', 'Themed Party Setup',
    'Prop Builders', 'Sign Makers', 'Invitation & Flyer Designers',
    'Graphic Designers', 'Custom T-Shirt Makers',
    'Apparel & Merch Customizers', 'Craft Makers',
    'Calligraphy / Hand Lettering Artists', 'Tablescape Designers',
    'Candle / Scent Gift Makers',
  ],
  beauty_styling: [
    'Makeup Artists', 'Hair Stylists', 'Henna Artists', 'Nail Artists',
    'Face & Body Glitter Artists', 'Barbers / Grooming Stylists',
    'Costume / Wardrobe Stylists',
  ],
  venue: ['Venue'],
  rentals: [
    'Bounce House / Inflatable Rentals', 'Water Slide Rentals',
    'Tent Rentals', 'Table & Chair Rentals', 'Linen Rentals',
    'Stage Rentals', 'Lighting & Sound Technicians', 'Equipment Rentals',
    'Luxury Portable Restroom Rentals', 'Throne Chair Rentals',
    'Marquee Letters / Light-Up Letters', 'Yard Sign Rentals',
    'Carnival Game Rentals', 'Casino Tables & Dealers', 'Game Truck',
    'Bounce Horse Racers',
  ],
  event_services: [
    'Event Planner', 'Day-Of Coordinator', 'Wedding Planner',
    'Officiant / Minister', 'Event Setup Crew', 'Security Staff',
    'Cleaning Crew', 'Parking Attendants',
    'Shuttle/Transportation Providers', 'Setup & Breakdown Teams',
    'Logistics / Delivery Services', 'Interactive Kids\' Activities',
    'Arts & Crafts Activity Hosts',
  ],
  other: [
    'Petting Zoo', 'Pony Rides', 'Reptile / Exotic Animal Shows',
    'Butterfly Release Providers', 'Animal Visits',
    'Fog / Snow Machine Providers', 'Firework Displays',
    'Confetti Cannon Providers', 'Cold Sparkler Fountain Providers',
    'Bubble Machines', 'Projection Mapping', 'Live Painters',
    'Custom Favor Makers', 'Gift Bag Assemblers',
    'Other Event Pro Services',
  ],
};

/**
 * Reverse lookup: category display label → vendor_type (best match).
 * First vendor_type (in VENDOR_TYPE_KEYS order) wins when a category
 * appears under multiple types.
 */
const _CATEGORY_TO_TYPE = (() => {
  const index = {};
  for (const key of VENDOR_TYPE_KEYS) {
    for (const cat of VENDOR_TYPE_TO_ALLOWED_CATEGORIES[key] || []) {
      if (!(cat in index)) index[cat] = key;
    }
  }
  return index;
})();

/**
 * Best-effort inference used for legacy vendors who were created before the
 * vendor_type field existed. The first matching category wins — consistent
 * with the backend `infer_vendor_type_from_categories`.
 */
export function inferVendorTypeFromCategories(categories) {
  if (!categories || !categories.length) return null;
  for (const cat of categories) {
    const t = _CATEGORY_TO_TYPE[cat];
    if (t) return t;
  }
  return null;
}

/**
 * Soft check: is this category a natural fit for the given vendor_type?
 * When vendor_type is not set, everything is considered a fit.
 */
export function isCategoryAllowedForType(vendorType, category) {
  if (!vendorType) return true;
  const allowed = VENDOR_TYPE_TO_ALLOWED_CATEGORIES[vendorType] || [];
  return allowed.includes(category);
}

/**
 * Sort a full category list so the allowed-for-type ones float to the top.
 * Everything else is preserved in its original order. Used by the profile
 * form category picker.
 */
export function sortCategoriesForType(categories, vendorType) {
  if (!vendorType) return [...(categories || [])];
  const allowed = new Set(VENDOR_TYPE_TO_ALLOWED_CATEGORIES[vendorType] || []);
  const primary = [];
  const rest = [];
  for (const c of categories || []) {
    if (allowed.has(c)) primary.push(c);
    else rest.push(c);
  }
  return { primary, rest };
}
